import numeral from 'numeral'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { IoInformationCircle } from 'react-icons/io5'

import { useUser } from 'src/providers/UserProvider'

import { Card } from 'src/components/atoms/Card'
import { ButtonLink } from 'src/components/atoms/Link'
import { LoadingSpinner } from 'src/components/atoms/LoadingSpinner'
import { Tooltip } from 'src/components/atoms/Tooltip'
import {
  Heading3,
  Heading5,
  ParagraphNoSpace
} from 'src/components/atoms/Typography'
import { HistoryGraph } from 'src/components/molecules/HistoryGraph'
import { LabelledCheckbox } from 'src/components/molecules/LabelledCheckbox'
import { PaginationRebrand } from 'src/components/molecules/PaginationRebrand'
import { ZeroState } from 'src/components/molecules/ZeroState'
import { FlowStepper } from 'src/components/organisms/FlowStepper'
import { DashboardApplicationsTable } from 'src/components/organisms/tables/DashboardApplicationsTable'

import {
  useGetAccountAndPlanterRegistrationQuery,
  useGetDashboardDataQuery,
  useGetDashboardTabsDataQuery,
  useListOpenSeasonsQuery
} from 'src/config/generated/graphql'
import {
  listProjectsPath,
  viewAccountPlanterRegistrationPath,
  viewApplicationPath,
  viewProjectApplicationsPath,
  viewProjectDetailPath
} from 'src/config/paths'
import { useHasPlanterExperience } from 'src/hooks/useHasPlanterExperience'
import { usePagination } from 'src/hooks/usePagination'
import { ApplicationWorkflowState } from 'src/types/application'
import { PlantingWorkflowState } from 'src/types/planting'

import { buildPlanterGraphData } from '../../helper'
import { GettingStartedGuides } from './GettingStartedGuides'
import { PlanterActionPanel } from './PlanterActionPanel'
import PlanterTimeline from './PlanterTimeline'
import { getSteps, gettingStartedLinks } from './helper'

const PlanterDashboardPanel = () => {
  const { user } = useUser()
  const { hasPlanterExperience, planterExperience } = useHasPlanterExperience()
  const { limit, currentPage, offset, onPageClick, resetPagination } =
    usePagination({
      limit: 5
    })

  const [isShowArchived, setIsShowArchived] = useState(false)

  useEffect(() => {
    resetPagination()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowArchived])

  const onShowArchived = (e: ChangeEvent<HTMLInputElement>) => {
    setIsShowArchived(e.target.checked)
  }

  const { data: dashboardTabsData, loading: dashboardTabsDataLoading } =
    useGetDashboardTabsDataQuery({
      fetchPolicy: 'cache-and-network',
      variables: {
        accountId: user?.Account?.Id,
        limit,
        offset,
        isShowArchived
      }
    })

  const { data: dashboardData } = useGetDashboardDataQuery()

  const { data: accountData, loading: accountDataLoading } =
    useGetAccountAndPlanterRegistrationQuery({
      variables: {
        id: user?.ActiveAccountId
      }
    })

  const totalCount = useMemo(() => {
    return dashboardTabsData?.Application_aggregate?.aggregate?.count || 0
  }, [dashboardTabsData?.Application_aggregate])

  const projectCount = useMemo(
    () => accountData?.Account_by_pk?.Planter?.Projects.length || 0,
    [accountData]
  )

  const defaultProject = useMemo(
    () => accountData?.Account_by_pk?.Planter?.Projects.slice(0, 1)[0],
    [accountData]
  )

  const hasPastProjects = useMemo(() => {
    return (
      accountData?.Account_by_pk?.Planter?.Projects.some(p =>
        p.Applications.some(a =>
          // Number(a.Season?.Name) <= dayjs().year() &&
          [
            ApplicationWorkflowState.Approved,
            ApplicationWorkflowState.Planted,
            ApplicationWorkflowState.Monitoring,
            ApplicationWorkflowState.Declined,
            ApplicationWorkflowState.Withdrawn
          ].includes(a.WorkflowState as ApplicationWorkflowState)
        )
      ) || false
    )
  }, [accountData])

  const hasApprovedRequest = useMemo(() => {
    return (
      accountData?.Account_by_pk?.Planter?.Projects.some(p =>
        p.Applications.some(a =>
          [
            ApplicationWorkflowState.Approved,
            ApplicationWorkflowState.Planted,
            ApplicationWorkflowState.Monitoring
          ].includes(a.WorkflowState as ApplicationWorkflowState)
        )
      ) || false
    )
  }, [accountData])

  const hasPendingRequest = useMemo(
    () =>
      accountData?.Account_by_pk?.Planter?.Projects.some(p =>
        p.Applications.some(
          a => a.WorkflowState === ApplicationWorkflowState.Pending
        )
      ),
    [accountData]
  )

  const canRequestTrees = useMemo(
    () =>
      accountData?.Account_by_pk?.Planter?.Projects.some(p =>
        p.Applications.some(
          a =>
            ![
              ApplicationWorkflowState.Pending,
              ApplicationWorkflowState.Declined,
              ApplicationWorkflowState.Approved
            ].includes(a.WorkflowState as ApplicationWorkflowState)
        )
      ),
    [accountData]
  )

  const hasValidPlanterExperience = useMemo(
    () => hasPlanterExperience && planterExperience?.WorkflowState !== 'Draft',
    [hasPlanterExperience, planterExperience]
  )

  const planterWorkflowState =
    planterExperience?.WorkflowState as PlantingWorkflowState

  const steps = useMemo(
    () =>
      getSteps({
        defaultProject,
        hasApprovedRequest,
        hasPastProjects,
        hasValidPlanterExperience,
        projectCount,
        planterWorkflowState
      }),
    [
      defaultProject,
      hasApprovedRequest,
      hasPastProjects,
      hasValidPlanterExperience,
      projectCount,
      planterWorkflowState
    ]
  )

  const { data: seasonData } = useListOpenSeasonsQuery()
  const openSeason = useMemo(() => {
    if (seasonData?.Season.length) {
      return seasonData.Season[seasonData.Season.length - 1]
    }
    return null
  }, [seasonData])

  // the order of the if statements in this function is important - don't change
  const panelContent = useMemo(() => {
    if (hasApprovedRequest) {
      return (
        <>
          <Card className='flex-1'>
            <div className='text-mostlyGreen font-bold flex flex-col gap-[10px]'>
              <span className='text-[30px] md:text-[40px] leading-none'>
                {numeral(
                  dashboardData?.PlanterStatsForUser[0]?.TreesApproved || 0
                ).format('0,0')}
              </span>
              <span className='text-[16px] md:text-[20px] leading-none'>
                Native trees approved
              </span>
            </div>
            <HistoryGraph data={buildPlanterGraphData(dashboardData)} />
          </Card>
          <Card
            size='large'
            className='w-full lg:w-[380px] flex flex-col gap-[10px]'
          >
            <Heading3 className='m-0! text-[24px] leading-[30px] md:text-[28px] md:leading-[39px] font-semibold'>
              Request native trees
            </Heading3>
            <PlanterActionPanel />
          </Card>
        </>
      )
    }
    if (hasPendingRequest) {
      const defaultApplication = defaultProject?.Applications.find(
        a => a.WorkflowState === ApplicationWorkflowState.Pending
      )
      return (
        <>
          <Card className='flex-1 flex flex-col gap-5'>
            <Heading3 className='m-0! text-[24px]! leading-[30px]! font-bold! md:text-[28px]! md:leading-[39px]! md:font-semibold!'>
              We have your request
            </Heading3>
            <ParagraphNoSpace className='font-medium text-[14px]! md:text-[16px]! leading-[20px]! md:leading-[24px]!'>
              Log back in here to see the status of your request. You’ll hear
              from us when we start assessing all requests.
            </ParagraphNoSpace>
            <Heading5>You can view your information anytime</Heading5>
            <ul className='list-normal'>
              <li>
                <ButtonLink
                  variant='text'
                  className='no-underline text-[14px] leading-[20px] md:text-[16px] md:leading-[24px] font-medium hover:underline'
                  href={viewAccountPlanterRegistrationPath}
                >
                  Planting experience
                </ButtonLink>
              </li>
              <li>
                <ButtonLink
                  variant='text'
                  className='no-underline text-[14px] leading-[20px] md:text-[16px] md:leading-[24px] font-medium hover:underline'
                  href={
                    defaultProject
                      ? viewProjectDetailPath({
                          routeParams: {
                            projectId: defaultProject?.Id
                          }
                        })
                      : listProjectsPath
                  }
                >
                  Planting project details
                </ButtonLink>
              </li>

              <li>
                <ButtonLink
                  variant='text'
                  className='no-underline text-[14px] leading-[20px] md:text-[16px] md:leading-[24px] font-medium hover:underline'
                  href={
                    defaultProject && defaultApplication
                      ? viewApplicationPath({
                          routeParams: {
                            projectId: defaultProject?.Id,
                            applicationId: defaultApplication.Id
                          }
                        })
                      : viewProjectApplicationsPath({
                          routeParams: {
                            projectId: defaultProject?.Id
                          }
                        })
                  }
                >
                  Tree request{' '}
                  {defaultProject && defaultApplication
                    ? `(ref: ${defaultApplication?.Reference})`
                    : ''}
                </ButtonLink>
              </li>
            </ul>
          </Card>
          <GettingStartedGuides links={gettingStartedLinks} />
        </>
      )
    }
    if (canRequestTrees) {
      return (
        <>
          <Card className='flex-1'>
            <ZeroState
              title='Start your planting journey!'
              description='Request, receive and manage your native trees.'
              image='/img/zero-states/planter.png'
            />
          </Card>
          <Card
            size='large'
            className='w-full lg:w-[380px] flex flex-col gap-[10px]'
          >
            <Heading3 className='m-0! text-[24px] leading-[30px] md:text-[28px] md:leading-[39px] font-semibold'>
              Request native trees
            </Heading3>
            <PlanterActionPanel />
          </Card>
        </>
      )
    }

    return (
      <>
        <Card className='flex-1 flex flex-col gap-5'>
          <Heading3 className='m-0! text-[24px]! leading-[30px]! font-bold! md:text-[28px]! md:leading-[39px]! md:font-semibold!'>
            Apply for trees for next season
          </Heading3>
          <ParagraphNoSpace className='font-medium text-[14px]! md:text-[16px]! leading-[20px]! md:leading-[24px]!'>
            If your application was declined, you can always try again for the
            next season.
          </ParagraphNoSpace>
          <Heading5>View information you’ve provided previously</Heading5>
          <ul className='list-normal'>
            <li>
              <ButtonLink
                variant='text'
                className='no-underline text-[14px] leading-[20px] md:text-[16px] md:leading-[24px] font-medium hover:underline'
                href={viewAccountPlanterRegistrationPath}
              >
                Planting experience
              </ButtonLink>
            </li>
            <li>
              <ButtonLink
                variant='text'
                className='no-underline text-[14px] leading-[20px] md:text-[16px] md:leading-[24px] font-medium hover:underline'
                href={
                  defaultProject
                    ? viewProjectDetailPath({
                        routeParams: {
                          projectId: defaultProject?.Id
                        }
                      })
                    : listProjectsPath
                }
              >
                Planting project
              </ButtonLink>
            </li>
          </ul>
        </Card>
        <Card
          size='large'
          className='w-full lg:w-[380px] flex flex-col gap-[10px]'
        >
          <Heading3 className='m-0! text-[24px] leading-[30px] md:text-[28px] md:leading-[39px] font-semibold'>
            Request native trees
          </Heading3>
          <PlanterActionPanel />
        </Card>
      </>
    )
  }, [
    canRequestTrees,
    dashboardData,
    defaultProject,
    hasApprovedRequest,
    hasPendingRequest
  ])

  const hasApplications = useMemo(
    () =>
      accountData?.Account_by_pk?.Planter?.Projects.reduce(
        (acc, curr) => acc + curr.Applications.length || 0,
        0
      ),
    [accountData]
  )

  return (
    <LoadingSpinner isLoading={accountDataLoading}>
      {!hasApplications ? (
        <div className='flex flex-col gap-5 lg:gap-10'>
          <Card className='flex flex-col gap-10'>
            <FlowStepper steps={steps} />
          </Card>
          <div className='flex flex-col lg:flex-row gap-5 lg:gap-10'>
            <Card size='large' className='flex flex-col flex-1'>
              <ZeroState
                title='Start your planting journey!'
                description='Request, receive and manage your native trees.'
                image='/img/zero-states/planter.png'
              />
            </Card>
            <GettingStartedGuides narrow links={gettingStartedLinks} />
          </div>
        </div>
      ) : (
        <div className='flex flex-col gap-5 md:gap-10'>
          {openSeason && <PlanterTimeline />}
          <Card>
            <div className='flex justify-between items-center'>
              <Heading3 className='m-0! text-[24px]! leading-[30px]! font-bold! md:text-[28px]! md:leading-[39px]! md:font-semibold!'>
                Manage your tree requests
              </Heading3>
              {!!dashboardTabsData?.Application && (
                <div className='flex items-center'>
                  <LabelledCheckbox
                    className='mx-0!'
                    id='showArchivedCheckbox'
                    checked={isShowArchived}
                    onChange={e => onShowArchived(e)}
                    label='Include archived'
                  />
                  <Tooltip
                    placement='bottom'
                    overlay={
                      <div className='w-56 px-[5px] text-center'>
                        Draft and unsuccessful tree requests will be archived at
                        the end of each year
                      </div>
                    }
                  >
                    <IoInformationCircle
                      onClick={e => {
                        e.preventDefault()
                      }}
                      className='text-mostlyGreen w-5 h-5 inline ml-1 mb-1'
                    />
                  </Tooltip>
                </div>
              )}
            </div>
            <DashboardApplicationsTable
              applicationsData={dashboardTabsData?.Application || []}
              isLoading={dashboardTabsDataLoading}
            />
            {totalCount > limit ? (
              <PaginationRebrand
                page={currentPage}
                pageSize={limit}
                onChange={onPageClick}
                count={totalCount}
              />
            ) : null}
          </Card>

          <div className='flex flex-col lg:flex-row gap-5 md:gap-10'>
            {panelContent}
          </div>
        </div>
      )}
    </LoadingSpinner>
  )
}

export default PlanterDashboardPanel
